/**
 * Chat List API
 *
 * Network-only function for fetching the user's past chat sessions.
 * Uses apiFetch for automatic JWT refresh on 401 and session re-init on 403.
 * NO Redux imports allowed in this file.
 */
import * as v from 'valibot';
import { CHAT_ENDPOINTS } from '@/api/endpoints';
import { ChatListResponseSchema, type ChatListRequestParams } from '@contracts';
import { apiFetch } from '@/api/utils';

type ChatListResponse = v.InferOutput<typeof ChatListResponseSchema>;

const DEFAULT_CHATS_LIMIT = 20;

/**
 * Fetch paginated list of user's past chat sessions.
 *
 * GET /api/resources/v1/chats
 *
 * @param params - limit and cursor for pagination
 * @returns ChatListResponse with chats array, pagination cursor, and hasMore flag
 */
export async function fetchChatsList(
  params: ChatListRequestParams = {},
): Promise<ChatListResponse> {
  const { limit = DEFAULT_CHATS_LIMIT, cursor } = params;

  const queryParams: Record<string, string> = { limit: String(limit) };
  if (cursor !== undefined) {
    queryParams['cursor'] = String(cursor);
  }

  const raw = await apiFetch<unknown>(CHAT_ENDPOINTS.CHATS_LIST, {
    method: 'GET',
    queryParams,
    skipCommonParams: true,
  });

  return v.parse(ChatListResponseSchema, raw);
}
